/**
 * SPX NET-PREMIUM PANEL — the pure half (R677①, BUILDER-bj).
 *
 * Consumes `/api/spx/net-prem` (`workers/read-api/src/netprem.ts`, BUILDER-bg). The pane half
 * and its eye receipt are BLOCKED until that route lands; this half is not, and it is where
 * every way the panel could lie gets refused.
 *
 * ⛔⛔ WHY THIS MODULE REFUSES INSTEAD OF COPING, which is the whole design:
 *
 * `gex2.ts`'s header records the defect this file exists to avoid. A pane written against a
 * DESCRIBED contract guessed three field names wrong; two failed loudly on first render and one
 * — `age_s` — did not fail at all. It rendered a pane with **no freshness chip**, and a pane
 * with no chip looks exactly like a healthy pane.
 *
 *   ⇒ ***THE FIELD THAT HURTS IS THE ONE WHOSE ABSENCE IS INDISTINGUISHABLE FROM HEALTH.***
 *
 * So the mandatory labels below are not read defensively with fallbacks. A fallback is how a
 * missing `expiry_scope` becomes a panel that quietly renders an ALL-EXPIRY tide with no scope
 * on it — which is SPX-066's defect exactly, arrived at by omission rather than by intent.
 * Every one of them is a REFUSAL with a named reason (law 1: fail LOUD, never blank, never a
 * degraded mode without a visible state).
 *
 * ⚠ I did NOT guess this contract — I read `buildNetPremBody` at `dd4a73f` and took the field
 * names from the producer. That makes the names exact and the SHAPE still a hypothesis until
 * the route serves: `readNetPrem` is therefore total over `unknown`, not over an assumed type.
 *
 * THE FOUR WAYS THIS PANEL WOULD BECOME A LIE, each with a refusal and an arm:
 *   1. Dropping ALL-EXPIRY  → it reads as the 0DTE tide (SPX-066 ruled that is a DIFFERENT
 *                             series, from flow-per-strike-intraday, NOT this route).
 *   2. Dropping HYPOTHESIS  → an unstudied display reads as a calibrated signal (law 6).
 *   3. Backfill as live     → 20 backfilled sessions render as continuous live history (R566).
 *   4. A silent staleness   → no chip is what healthy looks like (law 10, and gex2's lesson).
 */

/** Ruled by SPX-066 and asserted on every response by the route. */
export const EXPIRY_SCOPE_ALL = "ALL-EXPIRY";

/** Law 2: this pane renders SPX. SPXW is a different root and is never pooled into it. */
export const PANEL_ROOT = "SPX";

/** Capture eras the route distinguishes. `backfill` must never be presented as live history. */
export type NetPremEra = "live" | "backfill";

export interface NetPremBucketView {
  /** Law 2: the root rides every ROW, not just the envelope, so extraction cannot pool SPX/SPXW. */
  readonly root: string;
  readonly tapeTime: string;
  readonly marketDate: string;
  readonly netCallPremium: number;
  readonly netPutPremium: number;
  readonly cumNetCallPremium: number;
  readonly cumNetPutPremium: number;
  /** The pane's volume-delta bars read these. Validated here so the pane never has to bypass. */
  readonly netDelta: number;
  readonly callVolume: number;
  readonly putVolume: number;
  readonly era: NetPremEra;
}

export interface NetPremView {
  readonly ok: true;
  /** Law 2: carried into the view so a consumer of THIS object still knows its root. */
  readonly root: string;
  /** Rendered verbatim beside the title. Never abbreviated, never inferred. */
  readonly expiryScope: string;
  readonly expiryNote: string;
  readonly studyStatus: string;
  readonly displayOnly: boolean;
  readonly displayOnlyReason: string | null;
  /** Chip inputs. `ageS` may legitimately be null (empty); `budgetS` may NOT be missing. */
  readonly ageS: number | null;
  readonly budgetS: number;
  readonly stale: boolean;
  readonly empty: boolean;
  /** Why the panel is empty, in the route's own words. Distinguishes absence from REFUSAL. */
  readonly emptyReason: string | null;
  /** http-200 snapshots the route refused as malformed. Loud even at 0 — never hidden. */
  readonly malformedExcluded: number;
  readonly vendorTimeNewest: string | null;
  readonly capped: boolean;
  readonly interpolated: boolean;
  readonly counts: { readonly buckets: number; readonly live: number; readonly backfill: number };
  readonly buckets: readonly NetPremBucketView[];
}

export interface NetPremRefusal {
  readonly ok: false;
  /** Shown on screen. A refusal the operator cannot read is a blank panel with extra steps. */
  readonly reason: string;
}

export type NetPremVerdict = NetPremView | NetPremRefusal;

function refuse(reason: string): NetPremRefusal {
  return { ok: false, reason };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function readBucket(
  raw: unknown,
  index: number,
  envelopeRoot: string,
): NetPremBucketView | NetPremRefusal {
  if (!isRecord(raw)) return refuse(`bucket ${index} is not an object`);
  const era = raw.era;
  if (era !== "live" && era !== "backfill") {
    // ⛔ NOT defaulted to "live". An unknown era defaulted to live is defect 3 by omission.
    return refuse(`bucket ${index} has era ${JSON.stringify(era)}, which is neither live nor backfill`);
  }
  const root = raw.root;
  if (typeof root !== "string" || root.length === 0) {
    return refuse(`bucket ${index} carries no root — law 2 forbids a row that cannot say SPX vs SPXW`);
  }
  // ⛔⛔ PRESENCE IS NOT IDENTITY — `codex review` [P1], and it was right against a docstring in
  // THIS file that cites law 2. Checking only that a root EXISTS accepts `SPXW` into the SPX
  // panel, which is the pooling law 2 forbids, arrived at by a guard that looked like it held.
  if (root !== envelopeRoot) {
    return refuse(
      `bucket ${index} carries root ${JSON.stringify(root)} but the envelope says ` +
        `${JSON.stringify(envelopeRoot)} — SPX and SPXW are never pooled (law 2)`,
    );
  }
  const nums: Array<[string, unknown]> = [
    ["net_call_premium", raw.net_call_premium],
    ["net_put_premium", raw.net_put_premium],
    ["cum_net_call_premium", raw.cum_net_call_premium],
    ["cum_net_put_premium", raw.cum_net_put_premium],
    // ⛔ [P2]: the pane's volume-delta bars need these. A reader that silently drops them forces
    // the pane to bypass the "authoritative" parser, which makes it authoritative in name only.
    ["net_delta", raw.net_delta],
    ["call_volume", raw.call_volume],
    ["put_volume", raw.put_volume],
  ];
  for (const [name, value] of nums) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return refuse(`bucket ${index} has non-finite ${name} (${JSON.stringify(value)})`);
    }
  }
  const tapeTime = raw.tape_time;
  if (typeof tapeTime !== "string") return refuse(`bucket ${index} has no tape_time`);
  return {
    root,
    tapeTime,
    marketDate: typeof raw.market_date === "string" ? raw.market_date : "",
    netCallPremium: raw.net_call_premium as number,
    netPutPremium: raw.net_put_premium as number,
    cumNetCallPremium: raw.cum_net_call_premium as number,
    cumNetPutPremium: raw.cum_net_put_premium as number,
    netDelta: raw.net_delta as number,
    callVolume: raw.call_volume as number,
    putVolume: raw.put_volume as number,
    era,
  };
}

/**
 * Total over `unknown`: the route is not landed, so this must never assume its own success.
 *
 * ⛔ ORDER MATTERS AND IS DELIBERATE. The mandatory LABELS are checked before the data, because
 * a payload full of correct buckets with no scope on it is the dangerous case — it renders
 * beautifully and says the wrong thing.
 */
export function readNetPrem(payload: unknown): NetPremVerdict {
  if (!isRecord(payload)) return refuse("net-prem payload is not an object");

  // 0. LAW 2 — the root, before anything else, because everything below is a claim ABOUT it.
  // ⛔ An empty bucket list makes this the ONLY root in the response, which is exactly when a
  // misrouted SPXW envelope would otherwise render as an empty SPX panel and look correct.
  const envelopeRoot = payload.root;
  if (typeof envelopeRoot !== "string" || envelopeRoot.length === 0) {
    return refuse("net-prem response carries no root — law 2 forbids an unrooted series");
  }
  if (envelopeRoot !== PANEL_ROOT) {
    return refuse(
      `net-prem response is rooted ${JSON.stringify(envelopeRoot)}, not ${PANEL_ROOT} — ` +
        "this pane renders SPX and SPXW is never pooled into it (law 2)",
    );
  }

  // 1. SPX-066 — the scope, before anything else.
  const expiryScope = payload.expiry_scope;
  if (typeof expiryScope !== "string" || expiryScope.length === 0) {
    return refuse(
      "net-prem response carries no expiry_scope — SPX-066 requires the scope on every " +
        "response, and a panel that cannot name its scope must not draw a tide",
    );
  }
  if (expiryScope !== EXPIRY_SCOPE_ALL) {
    return refuse(
      `net-prem expiry_scope is ${JSON.stringify(expiryScope)}, not ${EXPIRY_SCOPE_ALL} — ` +
        "this pane renders the ALL-EXPIRY context tide and nothing else",
    );
  }
  const expiryNote = payload.expiry_note;
  if (typeof expiryNote !== "string" || expiryNote.length === 0) {
    return refuse("net-prem response carries no expiry_note — the scope caveat must be readable");
  }

  // 2. Law 6 — HYPOTHESIS until studied. Absence must not render as calibrated.
  const studyStatus = payload.study_status;
  if (typeof studyStatus !== "string" || studyStatus.length === 0) {
    return refuse("net-prem response carries no study_status — law 6 forbids an unlabelled level");
  }
  /**
   * ⛔⛔ PRESENCE IS NOT VALIDITY — `codex review` [P1], the second time this exact shape caught
   * me in one file. §80's law: `Number.isFinite` asks whether a value was PRODUCED, never
   * whether it is an INSTANT. A non-empty-string check asks whether a label was PRODUCED, never
   * whether it is a label this panel may honour. `"CALIBRATED"` is a non-empty string.
   *
   * Law 6: a level renders CALIBRATED only after its study passes, and then only WITH
   * (filter · era · n). So CALIBRATED is refused unless that metadata is actually carried —
   * which today's producer does not serve, and therefore today's producer cannot claim it.
   * ⇒ this fails toward HYPOTHESIS, the direction that cannot mislead.
   */
  if (studyStatus !== "HYPOTHESIS") {
    if (studyStatus !== "CALIBRATED") {
      return refuse(
        `net-prem study_status is ${JSON.stringify(studyStatus)} — law 6 knows only ` +
          "HYPOTHESIS and CALIBRATED, and an unrecognised label must never render as either",
      );
    }
    const cal = payload.calibration;
    const complete =
      isRecord(cal) &&
      typeof cal.filter === "string" && cal.filter.length > 0 &&
      typeof cal.era === "string" && cal.era.length > 0 &&
      typeof cal.n === "number" && Number.isFinite(cal.n) && cal.n > 0;
    if (!complete) {
      return refuse(
        "net-prem claims CALIBRATED without (filter · era · n) — law 6 requires the study's " +
          "own terms beside the label, and a bare CALIBRATED is the label without the study",
      );
    }
  }
  const displayOnly = payload.display_only;
  if (typeof displayOnly !== "boolean") {
    return refuse("net-prem response carries no display_only flag — an unstudied panel must say so");
  }
  // ⛔ AND THE PAIRING, not just the two fields: HYPOTHESIS with display_only:false is a panel
  // that says "unstudied" in one field and "trust me" in the other. The banner reads the second.
  if (studyStatus === "HYPOTHESIS" && displayOnly !== true) {
    return refuse(
      "net-prem is HYPOTHESIS but not display_only — an unstudied tide must not be presented " +
        "as actionable (law 6)",
    );
  }

  // 3. Law 10 — a chip on every data panel. A missing budget is gex2's silent `age_s` again.
  const budgetS = payload.budget_s;
  if (typeof budgetS !== "number" || !Number.isFinite(budgetS) || budgetS <= 0) {
    return refuse(
      "net-prem response carries no usable budget_s — without it the staleness chip cannot be " +
        "drawn, and a panel with no chip is indistinguishable from a fresh one",
    );
  }
  const ageS = payload.age_s;
  if (ageS !== null && (typeof ageS !== "number" || !Number.isFinite(ageS))) {
    return refuse(`net-prem age_s is ${JSON.stringify(ageS)}, neither a number nor null`);
  }
  const stale = payload.stale;
  if (typeof stale !== "boolean") return refuse("net-prem response carries no stale verdict");
  const empty = payload.empty;
  if (typeof empty !== "boolean") return refuse("net-prem response carries no empty verdict");

  // 4. R566 — gaps are never interpolated, and the panel must be told so explicitly.
  const interpolated = payload.interpolated;
  if (interpolated !== false) {
    return refuse(
      `net-prem interpolated is ${JSON.stringify(interpolated)} — this pane draws measured ` +
        "buckets only and refuses a series that claims to have filled its own gaps",
    );
  }

  const malformedExcludedRaw = payload.malformed_excluded;
  if (typeof malformedExcludedRaw !== "number" || !Number.isFinite(malformedExcludedRaw)) {
    return refuse("net-prem response carries no malformed_excluded count — an exclusion is never silent");
  }

  const rawBuckets = payload.buckets;
  if (!Array.isArray(rawBuckets)) return refuse("net-prem buckets is not an array");

  const buckets: NetPremBucketView[] = [];
  for (let i = 0; i < rawBuckets.length; i += 1) {
    const parsed = readBucket(rawBuckets[i], i, envelopeRoot);
    if ("ok" in parsed && parsed.ok === false) return parsed;
    buckets.push(parsed as NetPremBucketView);
  }

  // ⛔ OLDEST-FIRST IS PART OF THE MEANING, not a preference. The route cumulates in this order
  // and asserts it; a reversed series renders a cumulative tide that runs backwards and still
  // looks like a chart. Re-asserted here because the panel is a SECOND consumer of that order.
  for (let i = 1; i < buckets.length; i += 1) {
    if (buckets[i].tapeTime < buckets[i - 1].tapeTime) {
      return refuse(
        `net-prem buckets are not OLDEST-FIRST: ${buckets[i].tapeTime} follows ` +
          `${buckets[i - 1].tapeTime} — a cumulative tide in the wrong order is nonsense that ` +
          "still renders",
      );
    }
  }

  const backfill = buckets.filter((b) => b.era === "backfill").length;
  const emptyReason = payload.empty_reason;
  return {
    ok: true,
    root: envelopeRoot,
    expiryScope,
    expiryNote,
    studyStatus,
    displayOnly,
    displayOnlyReason:
      typeof payload.display_only_reason === "string" ? payload.display_only_reason : null,
    ageS: ageS as number | null,
    budgetS,
    stale,
    empty,
    emptyReason: typeof emptyReason === "string" ? emptyReason : null,
    malformedExcluded: malformedExcludedRaw,
    vendorTimeNewest:
      typeof payload.vendor_time_newest === "string" ? payload.vendor_time_newest : null,
    capped: payload.capped === true,
    interpolated: false,
    counts: { buckets: buckets.length, live: buckets.length - backfill, backfill },
    buckets,
  };
}

/**
 * The age of the DATA, derived from the newest bucket's tape minute — NOT the capture clock.
 *
 * ⛔⛔ THIS EXISTS BECAUSE I MEASURED THE LIVE ROUTE AND MY OWN PANE WAS ABOUT TO LIE.
 * At 23:5xZ on 2026-08-24 the deployed route served:
 * ```
 *   age_s ............... 41,447  =  11.5h   <- OUR capture clock
 *   newest tape_time .... 2026-08-21T20:59Z  =  ** 75.0h old **
 *   vendor_time_newest .. null                <- the market clock is NOT served
 *   counts .............. {buckets: 390, backfill: 390, live: 0}
 * ```
 * `age_s` measures WHEN WE LAST LOOKED, not WHAT WE LAST GOT. Feeding it to the chip alone
 * renders "11h 30m" over tape that is three days old — a real number answering the wrong
 * question, and wrong in the FRESH direction, which is the one that misleads.
 *
 * ⇒ ***A STALENESS CLOCK THAT TRACKS THE POLL UNDER-REPORTS EXACTLY WHEN THE FEED HAS STOPPED***
 * — the single condition it exists to catch (CC-uu's line, measured independently at my hand).
 *
 * `TidePanel` §3 already ruled this for the SPY tide: both ages are shown, and each NAMES the
 * series it describes, because on that feed they differed by hours. Here they differ by 63.5.
 * The envelope's own `vendor_time_newest` was meant to be the second clock; it is null today,
 * so this derives the data clock from the buckets themselves and needs nothing added upstream.
 */
export function netPremDataAgeS(
  buckets: readonly NetPremBucketView[],
  nowMs: number,
): number | null {
  if (buckets.length === 0) return null;
  // Buckets are OLDEST-FIRST (asserted by the reader), so the newest tape minute is last.
  const t = Date.parse(buckets[buckets.length - 1].tapeTime);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, (nowMs - t) / 1000);
}

/**
 * The face's clock-gap span (ORDER_HUD_APP_BUILD_ALL_GREEN_1 D1 (a)): seconds the capture clock runs
 * younger than the data, ONLY when that gap exceeds the panel's own budget; null when either clock is
 * absent or the gap is within budget. A null clock is never a 0 gap.
 */
export function netPremClockGapLoudS(view: NetPremView, dataAgeS: number | null): number | null {
  if (dataAgeS === null || view.ageS === null) return null;
  const gap = dataAgeS - view.ageS;
  return gap > view.budgetS ? gap : null;
}

/**
 * The two cumulative tide paths, on ONE shared premium axis (law 10).
 *
 * ⛔ PURE, AND HERE RATHER THAN IN THE COMPONENT, BECAUSE RENDER MATH IS STILL A DECISION.
 * Inline in a `.svelte` it is unarmable: the repo's own split (`gex2.ts` beside `Gex2Panel`)
 * exists so the choices below can be driven. Three of them can lie:
 *   - fewer than 2 points is NOT a chart. Interpolating a single bucket into a line invents a
 *     trend from one observation.
 *   - a FLAT series has span 0. Scaling it to full height would paint noise as a move; it is
 *     drawn on the mid-line instead, and the caller is told `span === 0` so it can say so.
 *   - both series share `lo`/`hi` ACROSS call and put. Scaling them independently would make
 *     two different magnitudes look comparable — the hidden-second-axis defect law 10 forbids.
 */
export function netPremTidePaths(
  buckets: readonly NetPremBucketView[],
  width: number,
  height: number,
): { call: string; put: string; span: number } {
  if (buckets.length < 2) return { call: "", put: "", span: 0 };
  const vals = buckets.flatMap((b) => [b.cumNetCallPremium, b.cumNetPutPremium]);
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = hi - lo;
  const y = (v: number) => (span === 0 ? height / 2 : height - ((v - lo) / span) * height);
  const x = (i: number) => (i / (buckets.length - 1)) * width;
  const line = (pick: (r: NetPremBucketView) => number) =>
    buckets.map((r, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(pick(r)).toFixed(1)}`).join(" ");
  return { call: line((r) => r.cumNetCallPremium), put: line((r) => r.cumNetPutPremium), span };
}

/**
 * The one-line banner the pane prints above the chart. Kept pure so it is armed rather than
 * eyeballed — this string is the whole of SPX-066's protection at the point of render.
 */
export function netPremBanner(view: NetPremView): string {
  const parts = [`${view.expiryScope}`, view.studyStatus];
  if (view.displayOnly) parts.push("DISPLAY-ONLY");
  if (view.counts.backfill > 0) {
    // ⛔ NEVER a bare total. "1,560 buckets" over a mostly-backfilled window is a true number
    // that answers the wrong question — bg's own "a count is not coverage", at render time.
    parts.push(`${view.counts.live} live · ${view.counts.backfill} backfill`);
  }
  if (view.malformedExcluded > 0) parts.push(`⛔ ${view.malformedExcluded} malformed excluded`);
  return parts.join(" · ");
}
